import { useState, useEffect } from 'react';
import { 
  auth, 
  db, 
  collection, 
  query, 
  where, 
  onSnapshot,
  doc,
  getDoc,
  onAuthStateChanged
} from '../firebase';

export const useDashboard = () => {
  const [userData, setUserData] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [activeCreators, setActiveCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let userUnsubscribe = null;
    let campaignsUnsubscribe = null;
    let creatorsUnsubscribe = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      try {
        // Development mode - create mock user data if no user
        const isDevelopment = process.env.NODE_ENV === 'development' || 
                             window.location.hostname === 'localhost' || 
                             window.location.hostname === '127.0.0.1';

        if (user || isDevelopment) {
          setLoading(true);
          
          if (user) {
            // Set up real-time listener for user data (for credits updates)
            const userDocRef = doc(db, 'users', user.uid);
            userUnsubscribe = onSnapshot(userDocRef, 
              (userDocSnap) => {
                if (userDocSnap.exists()) {
                  setUserData(userDocSnap.data());
                }
              },
              (error) => {
                console.error("Error fetching user data:", error);
              }
            );

            // Set up real-time listener for campaigns
            const q = query(
              collection(db, 'campaigns'),
              where('userId', '==', user.uid)
            );
            
            campaignsUnsubscribe = onSnapshot(q, 
              (snapshot) => {
                const campaignsData = snapshot.docs.map(doc => ({
                  id: doc.id,
                  ...doc.data()
                }));
                setCampaigns(campaignsData);
              },
              (error) => {
                console.error("Error fetching campaigns:", error);
                setError(error);
              }
            );

            // Set up real-time listener for active creators
            const creatorsQ = query(
              collection(db, 'activeCreators'),
              where('userId', '==', user.uid)
            );
            
            creatorsUnsubscribe = onSnapshot(creatorsQ, 
              (snapshot) => {
                const creatorsData = snapshot.docs.map(doc => ({
                  id: doc.id,
                  ...doc.data()
                }));
                setActiveCreators(creatorsData);
              },
              (error) => {
                console.error("Error fetching creators:", error);
                setError(error);
              }
            );
          } else {
            // Development mode - use mock data
            console.log('Development mode: Using mock user data');
            setUserData({
              name: 'Development User',
              email: 'dev@example.com',
              userType: 'brand',
              profileImage: 'https://via.placeholder.com/150',
              credits: 100
            });
            setCampaigns([]);
            setActiveCreators([]);
          }

          setLoading(false);
        } else {
          setUserData(null);
          setCampaigns([]);
          setActiveCreators([]);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error in auth state change:', err);
        setError(err);
        setLoading(false);
      }
    });

    // Cleanup function
    return () => {
      unsubscribeAuth();
      if (userUnsubscribe) userUnsubscribe();
      if (campaignsUnsubscribe) campaignsUnsubscribe();
      if (creatorsUnsubscribe) creatorsUnsubscribe();
    };
  }, []);

  // Calculate dashboard metrics
  const metrics = {
    totalViews: campaigns.reduce((sum, campaign) => sum + (campaign.views || 0), 0),
    totalConversions: campaigns.reduce((sum, campaign) => sum + (campaign.conversions || 0), 0),
    activeCampaigns: campaigns.filter(campaign => campaign.status === 'active').length,
    activeCreatorsCount: activeCreators.length,
  };

  return {
    userData,
    campaigns,
    activeCreators,
    metrics,
    loading,
    error
  };
}; 